import { type MessageDescriptor } from 'react-intl-next';

export enum Team {
	SoftwareDevelopment = 'software-development',
	ProductManagement = 'product-management',
	Marketing = 'marketing',
	Design = 'design',
	ProjectManagement = 'project-management',
	Operations = 'operations',
	ITSupport = 'it-support',
	Finance = 'finance',
	HumanResources = 'human-resources',
	Legal = 'legal',
	Sales = 'sales',
	Other = 'other',
}

export type InviteUser = {
	email: string;
};
export type JtbdContent = {
	value: string;
};

export const TEAM_ID_MAP = {
	[Team.SoftwareDevelopment]: 'onboarding-helpers.team.software.development.modern.branding',
	[Team.ProductManagement]: 'onboarding-helpers.team.productManagement',
	[Team.Marketing]: 'onboarding-helpers.team.marketing.modern.branding',
	[Team.Design]: 'onboarding-helpers.team.design',
	[Team.ProjectManagement]: 'onboarding-helpers.team.projectManagement',
	[Team.Operations]: 'onboarding-helpers.team.operations.modern.branding',
	[Team.ITSupport]: 'onboarding-helpers.team.it.support.modern.branding',
	[Team.Finance]: 'onboarding-helpers.team.finance.modern.branding',
	[Team.HumanResources]: 'onboarding-helpers.team.human.resources.modern.branding',
	[Team.Legal]: 'onboarding-helpers.team.legal.modern.branding',
	[Team.Sales]: 'onboarding-helpers.team.sales.modern.branding',
	[Team.Other]: 'onboarding-helpers.team.other',
};

export enum FocusArea {
	PlanOutProjects = 'plan-out-projects',
	CreateTechnicalDocumentation = 'create-technical-documentation',
	CreateMarketingCampaigns = 'create-marketing-campaigns',
	ShareStatusUpdates = 'share-status-updates',
	BrainstormAndStrategize = 'brainstorm-and-strategize',
	PlanAndPrioritize = 'plan-and-prioritize',
	GetAlignment = 'get-alignment',
	ImproveTeamProcesses = 'improve-team-processes',
	ShareUpdatesWithStakeholders = 'share-updates-with-stakeholders',
	StrategizeOnMarketingContent = 'strategize-on-marketing-content',
	ShareWeeklyMeetingNotes = 'share-weekly-meeting-notes',
}

export const TemplateBlueprintModuleCompleteKey = {
	itProjectPoster:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:it-project-poster-blueprint',
	sprintPlanning:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:sprint-planning-meeting-blueprint',
	productRequirements:
		'com.atlassian.confluence.plugins.confluence-software-blueprints:requirements-blueprint',
	softwareArchitectureReview:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:software-architecture-review-blueprint',
	howToArticle:
		'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-how-to-article-blueprint',
	architectureDiagram:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:aws-architecture-diagram-blueprint',
	incidentCommunication:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:incident-communication-blueprint',
	experimentPlanAndResults:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:experiment-planning-and-results-blueprint',
	dailyStandUp:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:daily-stand-up-blueprint',
	projectPlan:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:projectplan-blueprint',
	strategicPlan:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:business-strategy-blueprint',
	productRoadmap:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:product-roadmap-blueprint',
	workingAgreements:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:working-agreements-blueprint',
	teamPoster:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:team-poster-blueprint',
	rolesAndResponsibilities:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:roles-responsibilities-blueprint',
	stakeholderCommunications:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:stakeholder-communications-blueprint',
	endOfWeekStatusReport:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:eow-status-report-blueprint',
	marketingCampaign:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-campaign-blueprint',
	marketingPlan:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-blueprint',
	brainstorming:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:brainstorming-with-remote-teams-blueprint',
	swotAnalysis:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:swot-analysis-blueprint',
	competitiveAnalysis:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:competitive-analysis-blueprint',
	meetingNotes:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-blueprint',
	daci: 'com.atlassian.confluence.plugins.confluence-business-blueprints:decisions-blueprint',
	customerJourneyMapping:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:customer-journey-mapping-blueprint',
	toDoList: 'com.atlassian.confluence.plugins.confluence-business-blueprints:to-do-list-blueprint',
	retrospective:
		'com.atlassian.confluence.plugins.confluence-software-blueprints:retrospectives-blueprint',
	devOpsRunbook:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:devops-runbook-blueprint',
	itsmKnownErrors:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-known-errors-blueprint',
	businessStatusUpdate:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:business-status-update-blueprint',
	workshopPlanner:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:workshop-planner-blueprint',
	designSystems:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:design-systems-blueprint',
	designReview:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:design-review-blueprint',
	businessPlanOnePager:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:business-plan-one-pager-blueprint',
	riskAssessmentMatrix:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:risk-assessment-blueprint',
	vendorAgreement:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:vendor-agreement-blueprint',
	visionToValues:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:vision-to-values-blueprint',
	careerDevelopmentPlan:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:career-development-plan-blueprint',
	hrKnowledgeBaseManagement:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:hr-knowledge-base-management-blueprint',
	salesAccountPlanning:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:account-planning-for-sales-blueprint',
	salesPortal:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:sales-portal-template-blueprint',
	smartGoals:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:smart-goals-blueprint',
	projectStatus:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:project-status-blueprint',
	itsmChangeManagement:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-change-management-blueprint',
	itsmPostIncidentReviews:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-pir-blueprint',
	itsmRunbook:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-runbook-blueprint',
	troubleshootingArticle:
		'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-troubleshooting-article-blueprint',
	mvpIdeation:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:mvp-ideation-blueprint',
	designSprint:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:design-sprint-blueprint',
	elevatorPitch:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:elevator-pitch-blueprint',
	mutualActionPlan:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:mutual-action-plan-blueprint',
	executiveBusinessReview:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:executive-business-review-blueprint',
	fiveWhysAnalysis:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:5-whys-analysis-blueprint',
	okrs: 'com.atlassian.confluence.plugins.confluence-business-blueprints:okr-blueprint',
	employeeHandbook:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:employee-handbook-blueprint',
	hiringProcess:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:hiring-process-blueprint',
	contentStrategy:
		'com.atlassian.confluence.plugins.confluence-business-blueprints:content-strategy-blueprint',
	blankPage: '',
} as const;

export enum Template {
	ITProjectPoster = 'it-project-poster',
	SprintPlanning = 'sprint-planning',
	ProductRequirements = 'product-requirements',
	SoftwareArchitectureReview = 'software-architecture-review',
	HowToArticle = 'how-to-article',
	ArchitectureDiagram = 'architecture-diagram',
	IncidentCommunication = 'incident-communication',
	ExperimentPlanAndResults = 'experiment-plan-and-results',
	DailyStandUp = 'daily-stand-up',
	ProjectPlan = 'project-plan',
	StrategicPlan = 'strategic-plan',
	ProductRoadmap = 'product-roadmap',
	BlankPage = 'blank-page',
	WorkingAgreements = 'working-agreements',
	TeamPoster = 'team-poster',
	RolesAndResponsibilities = 'roles-and-responsibilities',
	StakeholderCommunications = 'stakeholder-communications',
	EndOfWeekStatusReport = 'end-of-week-status-report',
	MarketingCampaign = 'marketing-campaign',
	MarketingPlan = 'marketing-plan',
	Brainstorming = 'brainstorming',
	SWOTAnalysis = 'swot-analysis',
	CompetitiveAnalysis = 'competitive-analysis',
	MeetingNotes = 'meeting-notes',
	DACI = 'daci',
	CustomerJourneyMapping = 'customer-journey-mapping',
	ToDoList = 'to-do-list',
	Retrospective = 'retrospective',
	DevOpsRunbook = 'dev-ops-runbook',
	ITSMKnownErrors = 'itsm-known-errors',
	BusinessStatusUpdate = 'business-status-update',
	WorkshopPlanner = 'workshop-planner',
	DesignSystems = 'design-systems',
	DesignReview = 'design-review',
	BusinessPlanOnePager = 'business-plan-one-pager',
	RiskAssessmentMatrix = 'risk-assessment-matrix',
	VendorAgreement = 'vendor-agreement',
	VisionToValues = 'vision-to-values',
	CareerDevelopmentPlan = 'career-development-plan',
	HRKnowledgeBaseManagement = 'hr-knowledge-base-management',
	SalesAccountPlanning = 'sales-account-planning',
	SalesPortal = 'sales-portal',
	SMARTGoals = 'smart-goals',
	ProjectStatus = 'project-status',
	ITSMChangeManagement = 'itsm-change-management',
	ITSMPostIncidentReviews = 'itsm-post-incident-reviews',
	ITSMRunbook = 'itsm-runbook',
	TroubleshootingArticle = 'troubleshooting-article',
	MVPIdeation = 'mvp-ideation',
	DesignSprint = 'design-sprint',
	ElevatorPitch = 'elevator-pitch',
	MutualActionPlan = 'mutual-action-plan',
	ExecutiveBusinessReview = 'executive-business-review',
	FiveWhysAnalysis = 'five-whys-analysis',
	OKRs = 'okrs',
	EmployeeHandbook = 'employee-handbook',
	HiringProcess = 'hiring-process',
}

export type SVGComponent = React.ComponentType<React.SVGProps<SVGSVGElement> & { title?: string }>;

export type TeamDataType = {
	id: Team;
	nameDescriptor: MessageDescriptor;
	capitalizedNameDescriptor: MessageDescriptor;
	icon: React.ReactNode | React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
};

export type FocusAreaDataType = {
	id: FocusArea;
	nameDescriptor: MessageDescriptor;
	description: MessageDescriptor;
	previewImg?: SVGComponent;
};

export type TemplateDataType = {
	id: Template;
	nameDescriptor: MessageDescriptor;
	description?: MessageDescriptor;
	blueprintModuleCompleteKey: string;
	previewImg?: SVGComponent;
};

export type ContentTypeDataType = {
	id: ContentType;
	nameDescriptor: MessageDescriptor;
	icon: SVGComponent;
	background: SVGComponent;
	backgroundDark: SVGComponent;
	videoSrc: string;
	videoPreviewSrc: string;
};

export enum AccountSetupScreens {
	TEAM = 'onboarding-account-setup-modal.teamScreen',
	TEAM_NO_SKIP = 'onboarding-account-setup-modal.teamScreen.branding',
	JTBD = 'onboarding-account-setup-modal.jtbdScreen',
	JTBD_MODERN_BRANDING = 'onboarding-account-setup-modal.jtbdScreen.branding',
	LOADING = 'onboarding-account-setup-modal.loadingScreen',
	LOADING_MODERN_BRANDING = 'onboarding-account-setup-modal.loadingScreen.branding',
	TEMPLATE = 'onboarding-account-setup-modal.templateScreen',
	TEMPLATE_MODERN_BRANDING = 'onboarding-account-setup-modal.templateScreen.branding',
	INVITE = 'onboarding-account-setup-modal.inviteScreen',
	INVITE_MODERN_BRANDING = 'onboarding-account-setup-modal.inviteScreen.branding',
	CONTENTTYPE = 'onboarding-account-setup-modal.contentTypeScreen', // founding admin
	NTH_USER_CONTENT_TYPE = 'onboarding-account-setup-modal.nthUserContentTypeScreen', // nth user
	SPACESTOFOLLOW = 'onboarding-account-setup-modal.spacesToFollowScreen',
	EDITION = 'onboarding-account-setup-modal.editionCommunicationScreen',
	AUTOPROVISIONING_EDITION = 'onboarding-account-setup-modal.autoprovisioningEditionScreen',
	JTBD_CONTENT = 'onboarding-account-setup-modal.jtbdContentScreen',
}

export enum ContentType {
	PAGE = 'page',
	LIVE_DOC = 'live-doc',
	WHITEBOARD = 'whiteboard',
	DATABASE = 'database',
}

// Separated from ContentType to prevent TypeScript errors due to mismatched keys
export enum NthUserContentType {
	PAGE = 'page',
	WHITEBOARD = 'whiteboard',
	LIVE_DOC = 'live-doc',
	DATABASE = 'database',
}

export type AvatarDataProps = {
	userId: string;
	name: string;
	src?: string;
};

export type Space = {
	iconUrl: string;
	id: string;
	key: string;
	name: string;
	avatarData?: AvatarDataProps[];
	recommendationType: RecommendationType;
};

export enum RecommendationType {
	POPULAR = 'popular',
	TEAM_SPACE = 'teammates',
}

export type FollowSpacesParams = {
	spaces: Space[];
	spacesLoading: boolean;
};

export enum ConfluenceEdition {
	FREE = 'FREE',
	PREMIUM = 'PREMIUM',
	STANDARD = 'STANDARD',
}

export type CcpPriceEstimate = {
	trialEndDate: string;
	billEstimate: string;
};

/**
 * A step component used in the account setup process.
 *
 * @template D - The type of data required by the step component. Passed in as a data prop.
 * @template C - The type of result returned by the step component. Passed to the onComplete callback.
 */
export type StepComponent<
	D extends Record<string, any>,
	C extends Record<string, any>,
> = React.ComponentType<{
	data: D;
	onComplete: (result: C) => void;
	progress: { totalSteps: number; currentStepIndex: number };
	onExpandModal?: (isExpanded: boolean) => void;
}>;
